import { getRecaptchaSiteKey } from "@/constants/appConstants";
import { useLogin } from "@/context/useLoginProps";
import { useRecaptchaV3 } from "@/hooks";
import {
  INDIAN_PHONE_NUMBER_REGEX,
  emailRegex,
  encryptPayload,
  getClientId,
  getSiteProps,
} from "@/utils";
import { trackLogin, trackOTPSent } from "@/utils/loginTracking";
import {
  Button,
  Eye,
  EyeHide,
  Input,
  Label,
} from "@monsterindia/global-ui-components";
import React, { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import Heading from "../../common/heading";
import MultipleEmailModal from "../../common/multipleEmailModal";
import SocialLogin from "../../common/socialLogin";
import { useCheckUserExistence, useLoginUser, useSendOTP } from "../query";
import { LoginData, LoginFormData, LoginMode, SelectedEmail } from "../type";

const LoginField: React.FC = () => {
  const [forgotPassClicked, setForgotPassClicked] = useState(false);
  const [showMultipleEmailModal, setShowMultipleEmailModal] = useState(false);
  const [loginMode, setLoginMode] = useState<LoginMode>("password");
  const [isEmailLogin, setIsEmailLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  const {
    mutate: checkUserExistence,
    data: userExistenceData,
    status: userExistenceStatus,
  } = useCheckUserExistence();

  const {
    mutate: loginWithRedirect,
    data: loginData,
    status: loginStatus,
    error: loginError,
  } = useLoginUser();

  const {
    mutate: sendOTP,
    data: sendOtpData,
    status: sendOtpStatus,
  } = useSendOTP();

  const checkExistenceLoading = userExistenceStatus === "pending";
  const sendOtpLoading = sendOtpStatus === "pending";
  const loginLoading = loginStatus === "pending";

  const {
    page,
    hostname,
    loginData: existingLoginData,
    showToast,
    setLoginStep,
    setLoginData,
    translation: { login, errorMessages },
    handleEventTracking,
  } = useLogin();

  const { executeRecaptcha } = useRecaptchaV3(
    getRecaptchaSiteKey(hostname),
    true,
  );

  const siteConfig = useMemo(() => getSiteProps(hostname), [hostname]);
  const isRexMonsterSite = siteConfig?.site_map === "rexmonster";

  const {
    control,
    register,
    formState: { errors },
    handleSubmit,
    trigger,
    setValue,
  } = useForm<LoginFormData>({
    defaultValues: {
      countryCode: "+91",
      userName: "",
      password: "",
    },
    mode: "onBlur",
  });

  const userName = useWatch({ control, name: "userName" });
  const password = useWatch({ control, name: "password" });
  const countryCode = useWatch({ control, name: "countryCode" });

  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const emailParam = params.get("email");
      const usernameParam = params.get("username");
      const uuidParam = params.get("uuid");
      if (emailParam && usernameParam && uuidParam) {
        setIsEmailLogin(false);
        setValue("userName", usernameParam.split("-")[1]);
        setValue("countryCode", `+${usernameParam.split("-")[0]}`);
        setLoginData(() => ({
          username: usernameParam.split("-")[1],
          countryCode: `+${usernameParam.split("-")[0]}` || "+91",
          selectedEmailFromPopup: { maskedEmail: emailParam, uuid: uuidParam },
        }));
        sendApiCall(`+${usernameParam.replace("-", "")}`, uuidParam);
      } else if (emailParam) {
        setValue("userName", emailParam);
      } else if (existingLoginData?.username) {
        setValue("userName", existingLoginData?.username);
      }
    }
  }, []);

  useEffect(() => {
    setLoginMode(isRexMonsterSite ? "otp" : "password");
  }, [isRexMonsterSite]);

  useEffect(() => {
    if (!userExistenceData) return;

    if (userExistenceData.message !== "EXISTS") {
      showToast({
        show: true,
        type: "error",
        message: isEmailLogin
          ? "Email doesn't exist in the system."
          : "User not found with specified mobile number.",
      });
      return;
    }

    if (
      Array.isArray(userExistenceData.emails) &&
      userExistenceData.emails.length > 1
    ) {
      setShowMultipleEmailModal(true);
    } else {
      let username = isEmailLogin ? userName : `${countryCode}${userName}`;
      setShowMultipleEmailModal(false);
      if (loginMode === "otp" || forgotPassClicked) {
        sendApiCall(username);
        handleEventTracking({
          eventType: "eventPush",
          eventName: "Login_Input_Submitted",
          eventProps: {
            method: isEmailLogin ? "email" : "mobile",
          },
        });
      } else {
        const queryParams = new URLSearchParams(window.location.search);
        const client_id = queryParams.get("client_id");
        handleEventTracking({
          eventType: "eventPush",
          eventName: "Login_Password_Submitted",
          eventProps: {
            password_length: password.length,
          },
        });
        loginWithRedirect(
          {
            username,
            password,
            client_id: getClientId(client_id || undefined),
            queryParams,
          },
          {
            onError: (error) => {
              showToast({
                show: true,
                type: "error",
                message: error.message,
              });
            },
          },
        );
      }
    }
  }, [userExistenceData]);

  useEffect(() => {
    if (!loginData) return;
    if (loginData?.success && loginData?.redirectUrl) {
      // Track login success with centralized utility
      const username = isEmailLogin ? userName : `${countryCode}${userName}`;

      trackLogin({
        loginMethod: loginMode === "otp" ? "OTP" : "Password",
        credentialType: isEmailLogin ? "email" : "mobile",
        username: username,
      });

      isEmailLogin &&
        handleEventTracking({
          eventType: "onUserLoginPush",
          eventProps: {
            email: userName,
          },
        });
      handleEventTracking({
        eventType: "eventPush",
        eventName: "Login_Completed",
        eventProps: {
          method: "password",
        },
      });
      // Small delay to ensure cookies are set
      setTimeout(() => {
        window.location.href = loginData.redirectUrl;
      }, 100);
    }

    // Error case - show error message
    else if (loginData && !loginData.success) {
      const errorMessage =
        loginData?.message ||
        loginData?.error?.message?.errorMessage ||
        "Login failed. Please try again.";

      showToast({
        show: true,
        type: "error",
        message: errorMessage,
      });
    }
  }, [loginData]);

  useEffect(() => {
    if (loginError) {
      showToast({
        show: true,
        type: "error",
        message:
          loginError instanceof Error
            ? loginError.message
            : "Login failed. Please try again.",
      });
      handleEventTracking({
        eventType: "eventPush",
        eventName: "Login_Password_Error",
        eventProps: {
          error_type: loginError.message ?? "wrong_password",
        },
      });
    }
  }, [loginError]);

  useEffect(() => {
    if (sendOtpData && sendOtpData.id) {
      let showScreen = forgotPassClicked
        ? isEmailLogin || showMultipleEmailModal
          ? "FORGOT_PASSWORD_EMAIL"
          : "FORGOT_PASSWORD_NUM"
        : isEmailLogin || showMultipleEmailModal
          ? "VERIFY_EMAIL"
          : "VERIFY_PHONE";

      setLoginData((prevData: LoginData) => ({
        ...prevData,
        otpId: sendOtpData.id,
      }));
      setLoginStep(showScreen);
      setShowMultipleEmailModal(false);
    }
  }, [sendOtpData]);

  useEffect(() => {
    if (isRexMonsterSite) {
      const isOnlyDigits = /^\d{7,10}$/.test(userName || "");
      setIsEmailLogin(!isOnlyDigits);
    }
  }, [userName, isRexMonsterSite]);

  const validateMobileNumber = (value: string): boolean => {
    return INDIAN_PHONE_NUMBER_REGEX.test(value);
  };

  const validateEmail = (value: string): boolean => {
    return emailRegex.test(value);
  };

  const validateEmailOrMobileNumber = (value: string): string | boolean => {
    const isOnlyDigits = !!Number(value);

    if (!value) {
      return errorMessages.fieldRequired;
    }

    if (isRexMonsterSite) {
      if (isOnlyDigits) {
        if (value.length === 10) {
          if (validateMobileNumber(value)) {
            return true;
          } else {
            return login.phoneValidationError;
          }
        } else {
          return login.phoneValidationError;
        }
      } else {
        if (value.includes("@")) {
          // check if value contains "@" i.e. value is an email
          if (validateEmail(value)) {
            // run a regex to check if this is a valid email
            return true;
          } else {
            return login.emailValidationError;
          }
        } else {
          // if value doesn't contain "@" we can safely assume that this is not an email
          return login.validationError;
        }
      }
    } else {
      if (value.includes("@")) {
        if (validateEmail(value)) {
          // run a regex to check if this is a valid email
          return true;
        } else {
          return login.emailValidationError;
        }
      } else {
        return login.emailValidationError;
      }
    }
  };

  const sendApiCall = async (username: string, uuid: string = "") => {
    // Track OTP send with centralized utility
    trackOTPSent(isEmailLogin ? "email" : "mobile", username);

    const { reCaptchaToken, reCaptchaAction } = await executeRecaptcha("login");
    sendOTP(
      {
        username,
        reCaptchaToken,
        reCaptchaAction,
        ...(uuid && { uuid }),
      },
      {
        onError: (error) => {
          showToast({
            show: true,
            type: "error",
            message: error.message,
          });
        },
      },
    );
  };

  const handleFormSubmit = () => {
    const payload = isEmailLogin
      ? { email: userName }
      : { countryCode: "91", mobileNumber: userName };
    setLoginData(() => ({
      username: userName,
      countryCode: countryCode || "+91",
      isEmailLogin: isEmailLogin,
    }));
    checkUserExistence(
      {
        payload: encryptPayload(payload),
        isEmail: isEmailLogin,
        enabled: true,
      },
      {
        onError: (error) => {
          showToast({
            show: true,
            type: "error",
            message: error.message,
          });
        },
      },
    );
  };

  const handleMultipleEmailContinue = (args: SelectedEmail) => {
    sendApiCall(`${countryCode}${userName}`, args.uuid);
    setLoginData((prevData: LoginData) => ({
      ...prevData,
      selectedEmailFromPopup: args,
    }));
  };

  const labelText = isRexMonsterSite
    ? login.userNameForIndia
    : login.userNameForNonIndia;

  const placeholder = isRexMonsterSite
    ? login.userNamePlaceholderForIndia
    : login.userNamePlaceholderForNonIndia;

  return (
    <>
      <Heading
        title={page === "login" ? login.welcomeBack : login.logoutSuccess}
        text={login.methodToLogin}
      />
      <SocialLogin hostname={hostname} />

      <div className="my-6 flex items-center gap-4 text-xs leading-4">
        <div className="h-[1px] flex-1 bg-gray-200" />
        <span className="text-content-placeholder">or continue with</span>
        <div className="h-[1px] flex-1 bg-gray-200" />
      </div>

      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className="flex flex-col gap-8"
      >
        <Label labelText={labelText} className="text-sm" htmlFor="userName">
          <div className="flex gap-1">
            {!isEmailLogin && (
              <Input
                id="countryCode"
                value="+91"
                disabled
                className="w-[73px] text-center"
              />
            )}
            <Input
              id="userName"
              autoComplete="true"
              placeholder={placeholder}
              errorText={errors.userName?.message}
              {...register("userName", {
                validate: validateEmailOrMobileNumber,
              })}
            />
          </div>
        </Label>

        {loginMode === "password" && (
          <div>
            <Label
              labelText={login.password}
              className="text-sm"
              htmlFor="password"
            >
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                autoComplete="true"
                placeholder={login.passwordPlaceholder}
                errorText={errors.password?.message}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => {
                      const newShowPassword = !showPassword;
                      setShowPassword(newShowPassword);

                      if (
                        !newShowPassword &&
                        typeof window !== "undefined" &&
                        window.dataLayer
                      ) {
                        window.dataLayer.push({
                          event: "show_password",
                          eventCategory: "Log-In",
                          eventAction: "Show-Password",
                          eventLabel: "",
                        });
                      }
                    }}
                    className="cursor-pointer text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? (
                      <EyeHide width={20} height={20} />
                    ) : (
                      <Eye width={20} height={20} />
                    )}
                  </button>
                }
                {...register("password", {
                  required: {
                    value: true,
                    message: login.validPassword,
                  },
                  minLength: {
                    value: 6,
                    message: login.minLength,
                  },
                })}
              />
            </Label>

            <div className="mt-2 text-end">
              <span
                className="text-brand-primary cursor-pointer text-sm"
                onClick={async () => {
                  if (typeof window !== "undefined" && window.dataLayer) {
                    window.dataLayer.push({
                      event: "forgot_password",
                      eventCategory: "Log-In",
                      eventAction: "Forgot-Password",
                      eventLabel: "",
                    });
                  }
                  handleEventTracking({
                    eventType: "eventPush",
                    eventName: "Login_ForgotPassword_Initiated",
                    eventProps: {
                      via: isEmailLogin ? "email" : "mobile",
                    },
                  });
                  const valid = await trigger("userName");
                  if (valid && userName) {
                    handleFormSubmit();
                    setForgotPassClicked(true);
                  }
                }}
              >
                {login.forgotPassword}?
              </span>
            </div>
          </div>
        )}

        <Button
          size="lg"
          type="submit"
          id="loginSubmit"
          modifier="primary"
          aria-label="submit"
          className="rounded-full"
          disabled={!userName}
          label={loginMode === "password" ? login.loginText : login.sendOtp}
          isLoading={checkExistenceLoading || loginLoading || sendOtpLoading}
          onClick={() => {
            handleEventTracking({
              eventType: "eventPush",
              eventName:
                loginMode === "password"
                  ? "Login_Password_Clicked"
                  : "Login_OTP_Clicked",
              eventProps: {
                method: loginMode === "password" ? "password" : "otp",
              },
            });
          }}
        />
      </form>

      <div className="mt-5 text-center">
        <span
          className="text-brand-primary cursor-pointer text-sm font-medium"
          onClick={() => {
            const newMode = loginMode === "password" ? "otp" : "password";
            setLoginMode(newMode);
          }}
        >
          {loginMode === "password"
            ? login.loginViaOtp
            : login.loginViaPassword}
        </span>
      </div>

      <MultipleEmailModal
        show={showMultipleEmailModal}
        emails={userExistenceData?.emails || []}
        onClose={() => setShowMultipleEmailModal(false)}
        onContinue={handleMultipleEmailContinue}
        sendOtpLoading={sendOtpLoading}
      />
    </>
  );
};

export default LoginField;
